import React, { useState } from 'react';
import { Button, Tag, Tooltip, Empty } from 'antd';
import { UpOutlined, DownOutlined, WarningOutlined, CaretRightFilled, CaretDownFilled } from '@ant-design/icons'
import moment from 'moment';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { inputTypes, repeatObjects, verifyConditional } from '../../helper';
import i18next from 'i18next';
import Geolocation from '../geolocation';
import { GetIcon } from 'datastake-daf/dist/components';
import Link from './link';
import Select from './select';
import MultiSelect from './multiselect';
import AjaxSelect from './ajaxSelect';
import CheckboxGroup from './checkboxGroup';
import Total100 from './total100';
import ImageUpload from './imageUpload';
import Comment from './comment';
import { propHasValue } from '../../../../components/DynamicFormNew/helper';
import { openArmWindow } from '../../../../helpers/Window';
import { AuthenticationService } from '../../../../services';
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';

export const BasicInput = ({
  parent = null,
  name = null,
  config = {},
  data = {},
  allData = {},
  tooltip,
  linkingData = {},
  style = {},
  ajaxOptions,
  className = '',
  cols,
  repeatable = false,
  labelsOnly = false,
  valueOnly = false,
  actionActive = false,
  repeatableIndex = null,
  onAction = () => { },
  count = 1,
  heightIsFull = false,
}) => {
  const [extraOpen, setExtraOpen] = useState(false);
  const fullInputs = ['checkboxGroup', 'imageUpload'];
  const { t } = useTranslation();

  const label = (input = config) => {
    const lbl = input.outputLabel ? input.outputLabel : input.label;
    switch (typeof lbl) {
      case 'string':
        return lbl;
      case 'object':
        const hasData = Array.isArray(data) ? !!data.length : !!Object.keys(data).length;
        const labelKey = hasData ? (Object.keys(lbl).find(cond =>
          verifyConditional(cond, Array.isArray(data) ? data[0] : data, allData)) || Object.keys(lbl)[0]) : Object.keys(lbl)[0];
        return lbl[labelKey];
      default:
        return lbl;
    }
  }

  const comment = (input = config) => {
    const key = `${name}Comment`;
    return data[key] || undefined;
  }

  const placeholder = (item, name, withPlaceholder = true, cN) => {
    if (!withPlaceholder) {
      return '';
    }

    const style = repeatable ? { marginBottom: 0, width: '100%' } : {}
    const className = repeatable ? '' : 'text-muted';

    if (config.groupInputs) {
      if (config.groupInputs[name].spaces) {
        const space = '0';
        const spaces = [space];

        for (let i = 1; i < config.groupInputs[name].spaces; i++) {
          spaces.push(i.toString());
        }

        return (
          <>
            {config.noValue
              ? <p className={formatClassname([className, cN])} style={style} key={`${parent}-${name}`}>
                {config.noValue}
                {spaces.map((s) => <span key={s}>&nbsp;</span>)}
              </p>
              : <p className={formatClassname([className, cN])} style={style} key={`${parent}-${name}`}>
                {i18next.t('Unanswered')}
                {spaces.map((s) => <span key={s}>&nbsp;</span>)}
              </p>
            }
          </>
        )
      }
    }

    return config.noValue
      ? <p className={formatClassname([className, cN])} key={`${parent}-${name}`} style={style}>{config.noValue}</p>
      : repeatable ? <p className={formatClassname([className, cN])} key={`${parent}-${name}`} style={style}>--</p>
        : <p className={formatClassname([className, cN])} key={`${parent}-${name}`} style={style}>{i18next.t('Unanswered')}</p>;
  }

  const checkIfWhitespace = (str, placeholder) => {
    if (propHasValue(str) && str) {
      const s = str.toString();
      if (s === null || s.match(/^ *$/) !== null || s === '') {
        return placeholder;
      }
      return s;
    }
    return placeholder;
  };
  const hasValue = (str) => {
    return !(!str || /^\s*$/.test(str));
  }
  const getValue = (input, inputName, item, withPlaceholder = true, group) => {
    const plch = placeholder(item || data, inputName, withPlaceholder);
    let _v = null;

    switch (input.inputType) {
      case 'text':
      case 'phoneNumber':
        _v = item
          ? checkIfWhitespace(item[inputName], plch)
          : checkIfWhitespace(data[inputName], plch);

        break;
      case 'textarea':
        return <Comment
          tooltip={tooltip}
          comment={item
            ? checkIfWhitespace(item[inputName], plch)
            : checkIfWhitespace(data[inputName], plch)}
          isCoveredRiskComment={false}
          t={t}
        />;
      case 'link':
        const v = item
          ? (hasValue(item[inputName]) ? item[inputName] : placeholder(item, inputName, withPlaceholder))
          : (hasValue(data[inputName]) ? data[inputName] : placeholder(data, inputName, withPlaceholder))
        if (tooltip) {
          <Tooltip title={v}><Link v={v} /></Tooltip>
        }
        return <Link v={v} />;
      case 'number':
        _v = item
          ? (hasValue(item[inputName]) ? item[inputName] : placeholder(item, inputName, withPlaceholder))
          : (hasValue(data[inputName]) ? data[inputName] : placeholder(data, inputName, withPlaceholder))
        break;
      case 'percentage':
        _v = item
          ? (hasValue(item[inputName]) ? item[inputName] + '%' : placeholder(item, inputName, withPlaceholder))
          : (hasValue(data[inputName]) ? data[inputName] + '%' : placeholder(data, inputName, withPlaceholder));
        break;
      case 'select':
        return <Select
          item={item}
          input={input}
          tooltip={tooltip}
          inputName={inputName}
          withPlaceholder={withPlaceholder}
          data={data}
          placeholder={placeholder}
        />
      case 'ajaxSelect':
        return <AjaxSelect
          item={item}
          input={input}
          tooltip={tooltip}
          inputName={inputName}
          withPlaceholder={withPlaceholder}
          data={data}
          linkingData={linkingData}
          ajaxOptions={ajaxOptions}
        />;
      case 'switch':
        let val = item ? item[inputName] : data[inputName];
        if (!val) {
          val = false;
        }
        const label = config.options ? config.options.find(o => o.value === val) : val;
        _v = config.showOnTitle ? <Tag
          color={label.customColor ? label.customColor : val ? 'blue' : 'default'}
          style={{ minWidth: 120 }}
        >
          {typeof label === 'boolean' ? (
            label ? 'Yes' : 'No'
          ) : (label ? label.label : 'No')}
        </Tag> : label ? label.label : (val ? t('Yes') : t('No'));
        break;
      case 'button':
        return <Button
          ghost
          type="primary"
          size="small"
          className='no-ln-height'
          icon={actionActive ? <UpOutlined /> : <DownOutlined />}
          onClick={() => onAction(repeatableIndex)}>
          {actionActive ? i18next.t('View Less') : i18next.t('View More')}
        </Button>
      case 'multiselect':
        return <MultiSelect
          item={item}
          config={config}
          input={input}
          inputName={inputName}
          withPlaceholder={withPlaceholder}
          data={data}
          placeholder={placeholder}
        />;
      case 'radioGroup':
        const isSelected = item ? item[inputName] : data[inputName];
        const selectedOption = config.options.find((v) => v.value === isSelected);
        return selectedOption ? selectedOption.label : placeholder(item, inputName, withPlaceholder);
      case 'checkboxGroup':
        return <CheckboxGroup
          placeholder={placeholder}
          config={config}
          item={item}
          inputName={inputName}
          data={data}
          withPlaceholder={withPlaceholder}
        />
      case 'date':
        const currInput = item ? item[inputName] : data[inputName];
        _v = (propHasValue(currInput) ? moment(currInput).format('DD MMM YYYY').toString() : placeholder(item, inputName, withPlaceholder));
        break;
      case 'year':
        _v = item
          ? (hasValue(item[inputName]) ? moment(item[inputName]).format('YYYY').toString() : placeholder(item, inputName, withPlaceholder))
          : (hasValue(data[inputName]) ? moment(data[inputName]).format('YYYY').toString() : placeholder(data, inputName, withPlaceholder));
        break;
      case 'geolocation':
        return item
          ? (hasValue(item[inputName]) ? <Geolocation val={item[inputName]} /> : placeholder(item, inputName, withPlaceholder))
          : (hasValue(data[inputName]) ? <Geolocation val={data[inputName]} /> : placeholder(data, inputName, withPlaceholder));
      case 'tag':
        return item
          ? (hasValue(item[inputName]) ? item[inputName].map(tag => <Tag>{tag}</Tag>) : placeholder(item, inputName, withPlaceholder))
          : (hasValue(data[inputName]) ? data[inputName].map(tag => <Tag>{tag}</Tag>) : placeholder(data, inputName, withPlaceholder));
      case 'total100':
        return <Total100
          item={item}
          inputName={inputName}
          data={data}
          input={input}
          placeholder={placeholder}
          withPlaceholder={withPlaceholder}
        />
      case 'upload':
        try {
          if (tooltip) {
            const val = item ? hasValue(item[inputName]) ? item[inputName] : [] : hasValue(data[inputName]) ? data[inputName] : [];
            if (!val.length) {
              return '--';
            }

            if (val.length === 1) {
              return <a href={val[0].url}>{val[0].name}</a >
            }

            return <a href="/" onClick={(e) => {
              e.preventDefault();
              val.forEach((tag) => {
                window.open(tag.url);
              })
            }}>{t('Multiple')}</a >
          }
          return item
            ? (hasValue(item[inputName]) ? item[inputName].map(tag => {
              return <a href={tag.url}><Tag color="default">{tag.name}</Tag></a>
            }) : placeholder(item, inputName, withPlaceholder))
            : (hasValue(data[inputName]) ? data[inputName].map(tag => <a href={tag.url}><Tag>{tag.name}</Tag></a>) : placeholder(data, inputName, withPlaceholder))
        } catch (e) {
          console.log(e);
          return '';
        }
      case 'count':
        return count;
      case 'imageUpload':
        return <ImageUpload
          item={item}
          inputName={inputName}
          withPlaceholder={withPlaceholder}
          data={data}
          placeholder={placeholder}
        />;
      case 'website':
        const link = item ? data[inputName] : data[inputName];
        return link ?
          <a target="_blank" rel="noreferrer" className="website-link" href={link}>{link}</a>
          : <p className="text-muted" style={{ marginBottom: 0 }}>{t('Unanswered')}</p>;
      case 'armLink':
        return (
          <Button
            onClick={async () => {
              const res = await AuthenticationService.armToken();
              openArmWindow({
                url: `/proxy/analysis/asmos/${data.asmoId}?proxy_token=${res.data}`,
                name: 'Arm Proxy',
              })
            }}
          >
            View
          </Button>
        )
      default:
        return input.inputType;
    }

    if (_v) {
      if (tooltip && !group) {
        return (
          <Tooltip title={_v}>{_v}</Tooltip>
        )
      }

      return _v;
    }
  }
  const value = (item, input = config) => {
    switch (input.inputType) {
      case 'group':
        const keys = repeatObjects(config.groupInputs, (key) => key);
        const hasData = Object.keys(data).filter((v) => keys.includes(v)).length > 0;
        if (tooltip) {
          const _v = (
            <>
              {repeatObjects(config.groupInputs, (key, i) => {
                const first = i === 0;
                const space = '0';
                const spaces = [space];

                if (config.groupInputs[key].spaces) {
                  for (let i = 1; i < config.groupInputs[key].spaces; i++) {
                    spaces.push(i.toString());
                  }
                }

                const _v = item
                  ? (item[key] ? getValue(config.groupInputs[key], key, item, false, true) : (first ? placeholder(item, key, true, 'mr-1') : '--'))
                  : (data[key] ? getValue(config.groupInputs[key], key, data, false, true) : (first ? placeholder(data, key, true, 'mr-1') : '--'))
                return (
                  <>{_v}{' '}</>
                );
              })}
            </>
          )

          return <Tooltip title={_v}>{_v}</Tooltip>;
        }

        return (
          <div style={{ display: 'flex' }}>
            {repeatObjects(config.groupInputs, (key, i) => {
              const first = i === 0;
              let oLabel = '';
              if (config.groupInputs[key].showOutputLabel) {
                oLabel = label(config.groupInputs[key]);
              }
              // return item
              //   ? (item[key] ? <span>{getValue(config.groupInputs[key], key, item, false)}{oLabel}&nbsp;</span> : (first ? placeholder(item, key, true) : ''))
              //   : (data[key] ? <span>{getValue(config.groupInputs[key], key, data, false)}{oLabel}&nbsp;</span> : (first ? placeholder(data, key, true) : ''))
              const space = '0';
              const spaces = [space];

              if (config.groupInputs[key].spaces) {
                for (let i = 1; i < config.groupInputs[key].spaces; i++) {
                  spaces.push(i.toString());
                }
              }

              return item
                ? (item[key] ?
                  <span>
                    {getValue(config.groupInputs[key], key, item, false)}
                    {config.groupInputs[key].additionalText ? config.groupInputs[key].additionalText : ''}
                    {oLabel}
                    {spaces.map((s) => <span key={s}>&nbsp;</span>)}
                  </span>
                  : (first ? placeholder(item, key, true, 'mr-1') : ''))
                : (data[key] ?
                  <span>
                    {getValue(config.groupInputs[key], key, data, false)}
                    {config.groupInputs[key].additionalText ? ` ${config.groupInputs[key].additionalText}` : ''}
                    {oLabel}
                    {spaces.map((s) => <span key={s}>&nbsp;</span>)}
                  </span>
                  : (first ? placeholder(data, key, true, 'mr-1') : ''))
            })}
          </div>
        );
      default:
        return getValue(input, name, item, tooltip);
    }
  }

  const inputComment = comment();

  return !valueOnly ? <div
    className={`input ${className} ${name === 'action' ? 'action' : ''} ${config.borderBottomIsVisible ? 'border-bottom-input' : ''}`}
    style={!config.showOnTitle ?
      {
        width: repeatable ? (config.inputType === 'count' ? '50px' : (config.yesNoTag) ? config.yesNoTag : `calc(100% / ${cols} - ${!repeatable ? '10px' : '0px'})`) : '100%',
        flex: ((config.inputType !== 'count' && !config.yesNoTag) && repeatable) && '1',
        height: heightIsFull && '100%',
      }
      : {
        width: 'fit-content',
        position: 'absolute',
        right: 0,
      }
    }>
    {!config.showOnTitle ? <p className="label">
      {!config.dontShowLabel && (
        <>
          {tooltip ? label() : (
            <span>{label()}</span>
          )}
        </>
      )}
    </p> : null}
    {!repeatable ?
      name === 'action' ?
        <Button
          ghost
          type="primary"
          size="small"
          onClick={() => setExtraOpen(!extraOpen)}>
          {i18next.t('View More')}
        </Button>
        : <p className="value" style={!config.showOnTitle ? {} : {
          margin: 0,
        }}>{value()}</p>
      : !labelsOnly ? (
        (data).map(d => (
          <>
            <p className="value">
              {value(d)}
            </p>
            {extraOpen ? <div style={{ width: '100%', height: '200px' }}>
            </div> : null}
          </>
        ))
      ) : null}
    {(!config.coveredRiskComment && config.commentShownIfEmpty && !inputComment) && (
      <div style={{ marginTop: 16 }}>
        <p className="label">{config.commentOutputLabel || config.commentLabel || 'Details'}</p>
        <div style={{ marginTop: 8 }}>
          <p className="text-muted" style={{ fontSize: 14 }}>{t('No Comment')}</p>
        </div>
      </div>
    )}
    {config.comment ? (
      config.coveredRiskComment ? <Comment
        isCoveredRiskComment={true}
        comment={inputComment}
        label={config.commentOutputLabel || config.commentLabel}
        t={t}
      /> : (
        <div style={{ marginTop: 16 }}>
          <p className="label">{config.commentOutputLabel || config.commentLabel || 'Details'}</p>
          <div style={{ marginTop: 8 }}>
            <Comment
              isCoveredRiskComment={false}
              comment={inputComment}
              t={t}
            />
          </div>
        </div>
      )
    ) : null}
  </div> : (
    <div
      className={`input ${className} ${name === 'action' ? 'action' : ''}`}
      style={
        Object.assign(style, {
          width: config.yesNoTag ? config.yesNoTag : fullInputs.includes(config.inputType) ? '100%'
            : (config.inputType === 'count' ? '50px' : `calc(100% / ${cols} - ${!repeatable ? '10px' : '0px'})`),
          flex: (repeatable && config.inputType !== 'count' && !config.yesNoTag) && '1',
          height: heightIsFull && '100%',
        })
      }>
      <p
        className="value"
        style={{
          padding: config.inputType === 'count' && '8px 8px',
          display: config.canExpand && 'flex',
          height: heightIsFull && '100%',
        }}
      >
        {config.canExpand && (
          <>
            {!actionActive ? <CaretRightFilled style={{ color: 'var(--mmt-primary-70)', marginTop: 3 }} onClick={() => onAction(repeatableIndex)} />
              : <CaretDownFilled style={{ color: 'var(--mmt-primary-70)', marginTop: 3 }} onClick={() => onAction(repeatableIndex)} />
            }
          </>
        )
        }
        {value()}
        {config.comment && inputComment ? (
          <Tooltip placement="top" title={inputComment}>
            <Button
              style={{
                color: inputComment ? 'var(--color-primary-70)' : 'none',
              }}
              disabled={!inputComment}
              icon={
                <GetIcon style={{
                  color: inputComment ? 'var(--color-primary-70)' : 'none',
                  marginLeft: '8px'
                }}
                  icon={inputComment ? 'chat-quote-fill' : 'chat-quote-line'} />
              }
              type="link"
              size="small">
            </Button>
          </Tooltip>
        ) : null}
      </p>
    </div>
  );
}

const Input = ({
  name = null,
  parent = null,
  config = {},
  data = {},
  allData = {},
  linkingData = {},
  linkingForms = {},
  ajaxOptions = [],
  extraInputs = null,
  cols = 4,
  extraCols = 4,
  isRepeatable = false,
}) => {
  const excludeKeys = ['position', 'label'];
  const [hasOpenedExtra, setHasOpenedExtra] = useState(null);
  const sortTableCols = (form, sortKey) => {
    return Object.keys(form)
      .filter(key => propHasValue(form[key][sortKey]))
      .sort((a, b) => {
        return form[a][sortKey] - form[b][sortKey];
      })
      .reduce((f, key) => {
        if (propHasValue(form[key][sortKey])) {
          f[key] = form[key];
        }
        return f;
      }, {});
  }

  const getExtraTableCols = (form) => {
    const cols = Object.keys(form)
      .filter(key => propHasValue(form[key].table));
    return Object.keys(form)
      .filter(key => !cols.includes(key))
      .reduce((f, key) => {
        f[key] = form[key];
        return f;
      }, {});
  }

  const lastTableCol = (form) => {
    return Object.keys(form)
      .filter(key => propHasValue(form[key].table)).length
  }

  const filterForm = (form, data) => {
    if (!form.moduleScope) {
      return Object.keys(form).reduce((f, key) => {
        if (verifyConditional(form[key].showIf, data ? (parent && data[parent] ? data[parent] : data) : undefined, allData)) {
          f[key] = form[key];
        }
        return f;
      }, {})
    }
    return form;
  }

  const getModalContent = () => {
    const defaultKey = Object.keys(linkingForms[parent] || {})[0];
    const linkingD = linkingData ? (linkingData[config.dataLink.formNamespace] || {}) : {};
    const modalData = data[parent] ? (
      !Array.isArray(data[parent])
        ? typeof data[parent] === 'string'
          ? linkingD[data[parent]]
          : (linkingD[data[parent][name]] || {})
        : (data[parent].map(item => {
          if (item[name]) {
            Object.assign(item, {
              ...linkingD[item[name]]
            });
          }
          return item;
        }) || {})) : (isRepeatable ? [] : {});
    const form = Object.keys(linkingForms[parent][defaultKey])
      .reduce((f, key) => {
        const input = linkingForms[parent][defaultKey][key];
        if (!excludeKeys.includes(key)) {
          if (input.showIf && !propHasValue(input.table)) {
            if (verifyConditional(input.showIf, modalData, modalData)) {
              f[key] = linkingForms[parent][defaultKey][key];
            }
          } else {
            f[key] = linkingForms[parent][defaultKey][key];
          }
        }
        return f;
      }, {});
    if (extraInputs) {
      return getRepeatableContent(Object.assign(form, { ...extraInputs }), modalData);
    }
    if (isRepeatable) {
      const extraRepeatCols = getExtraTableCols(form);
      const hasExtraFields = isRepeatable && extraRepeatCols
        && Object.keys(extraRepeatCols).length;
      if (hasExtraFields) {
        Object.assign(config, {
          action: {
            label: '',
            inputType: 'button',
            table: lastTableCol(config),
          },
        });
      }
    }
    const formCfg = isRepeatable
      ? sortTableCols(form, 'table')
      : form;

    const totalCols = Object.keys(formCfg).length;
    return !isRepeatable ? (
      <>
        {repeatObjects(formCfg, (key, ind) => {
          const dataId = formCfg[key].dataId || key;
          return <BasicInput
            parent={name}
            key={key}
            name={dataId}
            config={form[key]}
            data={modalData ? modalData : (isRepeatable ? [] : {})}
            linkingData={linkingData}
            allData={data}
            style={(ind + 1) % 2 ? { marginRight: '16px' } : {}}
            cols={4}
            repeatable={isRepeatable}
            ajaxOptions={ajaxOptions}
          >
          </BasicInput>
        })}
        {isRepeatable && Array.isArray(modalData) && !modalData.length ? (
          <span className="text-muted font-italic"><WarningOutlined />&nbsp;{i18next.t('No data')}</span>
        ) : null}
      </>
    ) : <>
      {repeatObjects(formCfg, (key, i) => {
        const dataId = formCfg[key].dataId || key;
        return <BasicInput
          key={key}
          name={dataId}
          config={formCfg[key]}
          data={modalData ? modalData : (isRepeatable ? [] : {})}
          allData={data}
          repeatable={isRepeatable}
          cols={isRepeatable ? totalCols : 4}
          labelsOnly={true}
          ajaxOptions={ajaxOptions}
          linkingData={linkingData}
        >
        </BasicInput>
      })}
      {(!modalData || (Array.isArray(modalData) && !modalData.length)) ? (
        <span className="text-muted font-italic"><WarningOutlined /> {i18next.t('No Data')}</span>
      ) : (
        (Array.isArray(modalData) ? modalData : [modalData]).map((item, itemId) => (
          <>
            <div className={`repeatable-row ${itemId === hasOpenedExtra ? 'open' : ''}`} key={itemId}>
              {repeatObjects(formCfg, (key, i) => (
                <BasicInput
                  key={key}
                  name={formCfg[key].dataId || key}
                  linkingData={linkingData}
                  config={formCfg[key]}
                  data={item}
                  allData={data}
                  className={`${i === 0 ? 'first-col' : ''}`}
                  repeatable={isRepeatable}
                  cols={isRepeatable ? totalCols : 4}
                  valueOnly={true}
                  repeatableIndex={itemId}
                  actionActive={itemId === hasOpenedExtra}
                  ajaxOptions={ajaxOptions}
                >
                </BasicInput>
              ))}
            </div>
          </>
        ))
      )}
    </>;
  }

  const getRepeatableContent = (cForm, cData) => {
    let formConfig = cForm ? cForm : config;
    let formData = cData ? cData : data;

    if (Array.isArray(formData)) {
      formData = formData.filter((f) => f && Object.keys(f).length > 0);
    }

    const newFC = {
      count: {
        label: 'No.',
        inputType: 'count',
        table: 0,
      },
    };

    Object.entries(formConfig).forEach(([k, v]) => {
      newFC[k] = {
        ...v,
        table: (v.table || v.table === 0) ? v.table + 1 : undefined,
        canExpand: false,
      };
    });

    formConfig = newFC;

    const formCfg = isRepeatable
      ? formConfig
      : formConfig;

    const totalCols = Object.keys(formCfg).length;
    let count = 1;

    const _data = Array.isArray(formData) ? formData : [formData];

    return <>
      <div className="repeatable-header" style={{ width: '100%', display: 'flex' }}>
        {repeatObjects(formCfg, (key, i) => {
          const dataId = formCfg[key].dataId || key;
          return <BasicInput
            key={key}
            name={dataId}
            tooltip
            config={formCfg[key]}
            data={formData}
            allData={data}
            repeatable={isRepeatable}
            cols={isRepeatable ? totalCols : 4}
            labelsOnly={true}
            ajaxOptions={ajaxOptions}
            linkingData={linkingData}
            className={i + 1 === totalCols ? 'last-input' : ''}
            heightIsFull
          />
        })}
      </div>
      {(!formData || (Array.isArray(formData) && !formData.length)) ? (
        <div className="empty-state-repeatable">
          <Empty description={'No data yet...'} />
        </div>
      ) : (
        (_data).map((item, itemId) => (
          <>
            <div className={`repeatable-row ${itemId % 2 === 0 ? 'even' : ''} ${itemId === hasOpenedExtra ? 'open' : ''} ${itemId === _data.length - 1 ? 'last' : ''}`} key={itemId}>
              {repeatObjects(formCfg, (key, i) => {
                const input = (
                  <BasicInput
                    key={key}
                    count={formConfig[key].inputType === 'count' ? count++ : 1}
                    name={formConfig[key].dataId || key}
                    linkingData={linkingData}
                    config={formConfig[key]}
                    tooltip
                    data={item}
                    allData={data}
                    className={i === 0 ? 'first-col' : i + 1 === totalCols ? 'last-input' : ''}
                    repeatable={isRepeatable}
                    cols={isRepeatable ? totalCols : 4}
                    valueOnly={true}
                    repeatableIndex={itemId}
                    actionActive={itemId === hasOpenedExtra}
                    ajaxOptions={ajaxOptions}
                    onAction={(ev) => {
                      if (ev === hasOpenedExtra) {
                        setHasOpenedExtra(null);
                      } else {
                        setHasOpenedExtra(ev)
                      }
                    }}
                    heightIsFull
                  >
                  </BasicInput>
                )

                return input;
              })}
            </div>
          </>
        ))
      )}
    </>
  }

  const getComponent = () => {
    const fData = parent ? (config.dataId ? data[config.dataId] : data[parent]) : data;
    const form = filterForm(config, fData);
    delete data.status;
    const allFData = Object.assign(_.clone(fData) || {}, { ...data });
    switch (config.inputType) {
      case inputTypes.MODAL:
        if (config.showIf) {
          return verifyConditional(config.showIf, allFData, allData) ? getModalContent() : null;
        }
        return getModalContent();
      default:
        return isRepeatable
          ? getRepeatableContent()
          : <>
            {config.moduleScope ? (
              verifyConditional(config.showIf, allFData, allData) ? <BasicInput
                linkingData={linkingData}
                key={name}
                name={config.dataId || name}
                config={config}
                data={parent ? data[parent] : data}
                allData={data}
                repeatable={false}
                cols={2}
                ajaxOptions={ajaxOptions}
              /> : null
            ) : repeatObjects(form, (key, ind) => (
              <BasicInput
                linkingData={linkingData}
                key={`${key}-${ind}-${Date.now()}`}
                name={config[key].dataId || key}
                config={config[key]}
                data={data}
                allData={data}
                repeatable={false}
                cols={2}
                ajaxOptions={ajaxOptions}
              />
            ))}
          </>
    }
  }

  return getComponent();
}

export default Input;
